/**
 * Meta Pixel Adapter
 *
 * Handles event tracking for Meta (Facebook) Pixel.
 * Supports both standard and custom events.
 *
 * Setup required:
 * 1. Add Meta Pixel script to your site
 * 2. Configure pixel ID in environment variables
 * 3. Enable this adapter in tracking config
 *
 * @see https://developers.facebook.com/docs/meta-pixel
 */

'use client';

import type {
  TrackingAdapter,
  TrackingEvent,
  TrackingIdentity,
} from '../types';
import { getMetaEventName, isMetaStandardEvent } from '../events';

/**
 * Meta Pixel tracking adapter
 */
export class MetaAdapter implements TrackingAdapter {
  name = 'meta';

  /**
   * Check if Meta Pixel is loaded
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.fbq === 'function';
  }

  /**
   * Track an event to Meta Pixel
   */
  async track(event: TrackingEvent): Promise<void> {
    if (!this.isAvailable()) {
      console.warn('Meta Pixel not loaded');
      return;
    }

    try {
      const eventName = getMetaEventName(event.eventName);
      const eventData = this.formatEventData(event);

      // Generate or use provided event ID for deduplication
      const eventId = event.eventId || this.generateEventId();

      // CRITICAL: Pass eventID to pixel for deduplication with CAPI
      // Use standard event if available, otherwise custom event
      if (isMetaStandardEvent(event.eventName)) {
        window.fbq('track', eventName, eventData, { eventID: eventId });
      } else {
        window.fbq('trackCustom', eventName, eventData, { eventID: eventId });
      }

      // Also send to Conversions API (server-side) for better attribution
      await this.trackConversionsAPI(
        eventName,
        eventData,
        event.identity,
        eventId,
      );
    } catch (error) {
      console.error('Meta Pixel tracking error:', error);
      throw error;
    }
  }

  /**
   * Identify a user in Meta Pixel (Advanced Matching)
   * IMPORTANT: Do NOT call init() multiple times - use set() instead
   */
  identify(identity: TrackingIdentity): void {
    if (!this.isAvailable()) {
      console.warn('Meta Pixel not loaded');
      return;
    }

    try {
      const userData: Record<string, any> = {};

      // Advanced matching parameters
      // @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
      if (identity.email) {
        userData.em = identity.email.toLowerCase().trim();
      }

      if (identity.phone) {
        userData.ph = identity.phone.replace(/\D/g, '');
      }

      if (identity.firstName) {
        userData.fn = identity.firstName.toLowerCase().trim();
      }

      if (identity.lastName) {
        userData.ln = identity.lastName.toLowerCase().trim();
      }

      // External ID (user ID from your system)
      if (identity.userId) {
        userData.external_id = identity.userId;
      }

      // CRITICAL: Use 'set' instead of 'init' to update user data
      // init() should only be called once when pixel loads
      window.fbq('set', 'userData', userData);
    } catch (error) {
      console.error('Meta Pixel identify error:', error);
      throw error;
    }
  }

  /**
   * Format event data for Meta Pixel
   */
  private formatEventData(event: TrackingEvent): Record<string, any> {
    const data: Record<string, any> = {
      ...event.properties,
    };

    // Add product info
    if (event.product) {
      data.content_ids = [event.product.productId];
      data.content_name = event.product.name;
      data.content_type = 'product';
      data.content_category = event.product.category;
      data.value = event.product.price * (event.product.quantity || 1);
      data.currency = event.product.currency || 'USD';
      data.num_items = event.product.quantity || 1;
    }

    // Add order info (Purchase event)
    if (event.order) {
      data.value = event.order.total;
      data.currency = event.order.currency;
      data.content_ids = event.order.products?.map((p) => p.productId) || [];
      data.content_type = 'product';
      data.num_items =
        event.order.products?.reduce((sum, p) => sum + (p.quantity || 1), 0) ||
        0;
    }

    // Add value if present
    if (event.value !== undefined && data.value === undefined) {
      data.value = event.value;
    }

    return data;
  }

  /**
   * Get Meta Pixel ID from environment
   */
  private getPixelId(): string {
    // In production, this should come from env variables
    return process.env.NEXT_PUBLIC_META_PIXEL_ID || '';
  }

  /**
   * Server-side tracking via Conversions API
   *
   * This is CRITICAL for good conversion tracking post-iOS 14.5.
   * Server-side events have ~30% higher match rates.
   *
   * @see https://developers.facebook.com/docs/marketing-api/conversions-api
   */
  private async trackConversionsAPI(
    eventName: string,
    eventData: Record<string, any>,
    identity?: TrackingIdentity,
    eventId?: string,
  ): Promise<void> {
    try {
      // CRITICAL: Use same event ID from pixel call for deduplication
      // Meta will deduplicate automatically when IDs match
      const response = await fetch('/api/tracking/meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName,
          eventData,
          identity,
          eventId, // For deduplication
          sourceUrl: window.location.href,
          userAgent: navigator.userAgent,
          fbp: this.getFbpCookie(), // Facebook browser pixel cookie
          fbc: this.getFbcCookie(), // Facebook click ID cookie
        }),
      });

      if (!response.ok) {
        console.error('Meta Conversions API failed:', response.statusText);
      }
    } catch (error) {
      // Don't throw - server-side is supplementary
      console.error('Meta Conversions API error:', error);
    }
  }

  /**
   * Generate unique event ID for deduplication
   */
  private generateEventId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get Facebook browser pixel cookie (_fbp)
   */
  private getFbpCookie(): string | null {
    if (typeof document === 'undefined') return null;

    const match = document.cookie.match(/_fbp=([^;]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get Facebook click ID cookie (_fbc)
   */
  private getFbcCookie(): string | null {
    if (typeof document === 'undefined') return null;

    const match = document.cookie.match(/_fbc=([^;]+)/);
    return match ? match[1] : null;
  }
}

// Extend window type for Meta Pixel
declare global {
  interface Window {
    fbq: {
      (
        action: 'track',
        eventName: string,
        data?: Record<string, any>,
        options?: { eventID?: string },
      ): void;
      (
        action: 'trackCustom',
        eventName: string,
        data?: Record<string, any>,
        options?: { eventID?: string },
      ): void;
      (action: 'init', pixelId: string, userData?: Record<string, any>): void;
      (action: 'set', key: 'userData', data: Record<string, any>): void;
    };
  }
}
